import React from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import { DnsRecordsModal } from './DnsRecordsModal';
import { Mail, Globe, RefreshCw, Trash2 } from 'lucide-react';

export function DomainCard({ domain, onRefresh }) {
  const navigate = useNavigate();
  const [showDns, setShowDns] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const statusColors = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    active: 'bg-blue-50 text-blue-700 border-blue-200',
    suspended: 'bg-rose-50 text-rose-700 border-rose-200',
  };

  async function handleVerifyDns() {
    setVerifying(true);
    setError(null);

    try {
      await api.post(`/api/domains/${domain.id}/verify-dns`, {});
      // Refresh in 5 seconds
      setTimeout(onRefresh, 5000);
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to verify DNS';
      setError(message);
    } finally {
      setVerifying(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete domain ${domain.domain_name}? This cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await api.delete(`/api/domains/${domain.id}`);
      onRefresh();
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to delete domain';
      setError(message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900">{domain.domain_name}</h3>
            <p className="text-sm text-slate-500 mt-1">Added on {new Date(domain.created_at).toLocaleDateString()}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[domain.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
            {domain.status.toUpperCase()}
          </span>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-rose-50 text-rose-700 text-sm rounded-lg border border-rose-100">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/domains/${domain.id}/mailboxes`)}
              className="flex-1 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Mail size={16} />
              Manage Mailboxes
            </button>
            <button
              onClick={() => setShowDns(true)}
              className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Globe size={16} />
              DNS Records
            </button>
          </div>

          <div className="flex gap-2">
            {domain.status === 'pending' && (
              <button
                onClick={handleVerifyDns}
                disabled={verifying}
                className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <RefreshCw size={16} className={verifying ? "animate-spin" : ""} />
                {verifying ? 'Verifying...' : 'Verify DNS'}
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 text-rose-600 hover:bg-rose-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Trash2 size={16} />
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {showDns && (
        <DnsRecordsModal
          domain={domain}
          onClose={() => setShowDns(false)}
        />
      )}
    </div>
  );
}
